"use client";

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  RefreshCw,
  ChevronRight,
  ChevronDown,
  Building2,
  Loader2,
  Table2,
  GitBranch,
} from "lucide-react";
import { pipefyApi } from "@/lib/api-client";
import { toast } from "sonner";

const AREAS_DATABASE_ID = "306546622";

type RecordField = {
  name: string;
  value: string | null;
  field?: { id: string; label: string; type: string; internal_id: string };
};

function getFieldValue(
  record: { record_fields?: RecordField[] },
  fieldName: string
): string | null {
  const field = record.record_fields?.find(
    (f) => f.name === fieldName || f.field?.label === fieldName
  );
  const v = field?.value;
  if (v == null || v === "") return null;
  return String(v);
}

function getConnectorIds(value: string | null): string[] {
  if (!value) return [];
  const trimmed = value.trim();
  if (trimmed.startsWith("[")) {
    try {
      const arr = JSON.parse(trimmed) as string[];
      return Array.isArray(arr) ? arr.filter(Boolean) : [];
    } catch {
      return [];
    }
  }
  return trimmed ? [trimmed] : [];
}

function formatAssignee(value: string | null): string {
  if (!value) return "—";
  const t = value.trim();
  if (t.startsWith("[")) {
    try {
      const arr = JSON.parse(t) as string[];
      return Array.isArray(arr) ? arr.join(", ") : value;
    } catch {
      return value;
    }
  }
  return value;
}

interface TreeNode {
  id: string;
  nome: string;
  nivel: "Nível 1" | "Nível 2" | "Nível 3";
  raw: { record_fields?: RecordField[] };
  children: TreeNode[];
}

export interface AreaRow {
  id: string;
  nome: string;
  nivel: string;
  areaPai: string;
  diretor: string;
  gerente: string;
  usuario: string;
  observacoes: string;
}

function buildTree(
  records: { id: string; record_fields?: RecordField[] }[]
): TreeNode[] {
  const roots: TreeNode[] = [];
  const nivel1ByName = new Map<string, TreeNode>();
  const nivel2ByName = new Map<string, TreeNode>();
  const allNodes: { node: TreeNode; rec: (typeof records)[0] }[] = [];

  records.forEach((rec) => {
    const nome = getFieldValue(rec, "Área de Negócio") || "(sem nome)";
    const nivelRaw = getFieldValue(rec, "Nível área");
    if (!nivelRaw) return;
    const nivel = nivelRaw as TreeNode["nivel"];
    const node: TreeNode = { id: rec.id, nome, nivel, raw: rec, children: [] };
    allNodes.push({ node, rec });
    if (nivel === "Nível 1") nivel1ByName.set(nome, node);
    if (nivel === "Nível 2") nivel2ByName.set(nome, node);
  });

  allNodes.forEach(({ node, rec }) => {
    if (node.nivel === "Nível 1") {
      roots.push(node);
      return;
    }

    if (node.nivel === "Nível 2") {
      const parentName = getConnectorIds(getFieldValue(rec, "Diretoria"))[0];
      const parent = parentName ? nivel1ByName.get(parentName) : undefined;
      if (parent) {
        parent.children.push(node);
      } else {
        roots.push(node);
      }
      return;
    }

    if (node.nivel === "Nível 3") {
      const areaVincName = getConnectorIds(getFieldValue(rec, "Área de Negócio vinculada"))[0];
      let parent = areaVincName ? nivel2ByName.get(areaVincName) : undefined;

      if (!parent) {
        const dirName = getConnectorIds(getFieldValue(rec, "Diretoria"))[0];
        parent = dirName ? nivel1ByName.get(dirName) : undefined;
      }

      if (parent) {
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    }
  });

  const sortRecursive = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => a.nome.localeCompare(b.nome));
    nodes.forEach((n) => sortRecursive(n.children));
  };
  sortRecursive(roots);
  return roots;
}

function treeToFlatRows(tree: TreeNode[], parentName: string = ""): AreaRow[] {
  const rows: AreaRow[] = [];
  function walk(nodes: TreeNode[], pai: string) {
    nodes.forEach((node) => {
      const r = node.raw;
      rows.push({
        id: node.id,
        nome: node.nome,
        nivel: node.nivel,
        areaPai: pai,
        diretor: formatAssignee(getFieldValue(r, "Diretor Responsável")) ?? "—",
        gerente: formatAssignee(getFieldValue(r, "Gerente Responsável")) ?? "—",
        usuario: formatAssignee(getFieldValue(r, "Usuário Responsável")) ?? "—",
        observacoes: getFieldValue(r, "Observações") ?? "—",
      });
      walk(node.children, node.nome);
    });
  }
  walk(tree, "");
  return rows;
}

const LEVEL_COLORS = [
  { border: "border-blue-500", bg: "bg-blue-500/10", text: "text-blue-700 dark:text-blue-300", badge: "bg-blue-500 text-white" },
  { border: "border-emerald-500", bg: "bg-emerald-500/10", text: "text-emerald-700 dark:text-emerald-300", badge: "bg-emerald-500 text-white" },
  { border: "border-amber-500", bg: "bg-amber-500/10", text: "text-amber-700 dark:text-amber-300", badge: "bg-amber-500 text-white" },
] as const;

function OrgCard({
  node,
  level,
  expanded,
  onToggle,
}: {
  node: TreeNode;
  level: number;
  expanded: Set<string>;
  onToggle: (id: string) => void;
}) {
  const hasChildren = node.children.length > 0;
  const isExpanded = expanded.has(node.id);
  const colors = LEVEL_COLORS[Math.min(level, 2)];
  const diretor = formatAssignee(getFieldValue(node.raw, "Diretor Responsável"));

  return (
    <div className="flex flex-col items-center">
      <button
        type="button"
        onClick={() => hasChildren && onToggle(node.id)}
        className={`
          relative flex flex-col items-center rounded-xl border-2 shadow-sm
          px-4 py-3 min-w-[180px] max-w-[240px] transition-all
          ${colors.border} ${colors.bg}
          ${hasChildren ? "cursor-pointer hover:shadow-md hover:scale-[1.02]" : "cursor-default"}
        `}
      >
        <div className="flex items-center gap-1.5 mb-1">
          <Building2 className={`h-4 w-4 shrink-0 ${colors.text}`} />
          <span className={`font-semibold text-sm leading-tight text-center ${colors.text}`}>
            {node.nome}
          </span>
        </div>
        {diretor && diretor !== "—" && (
          <span className="text-[11px] text-muted-foreground truncate max-w-full">
            {diretor}
          </span>
        )}
        <span className={`mt-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full ${colors.badge}`}>
          {node.nivel}
        </span>
        {hasChildren && (
          <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 z-10 bg-background border rounded-full p-0.5 shadow-sm">
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
            )}
          </span>
        )}
      </button>

      {hasChildren && isExpanded && (
        <div className="flex flex-col items-center mt-1">
          <div className={`w-px h-5 ${colors.border.replace("border-", "bg-")}`} />

          {node.children.length === 1 ? (
            <OrgCard
              node={node.children[0]}
              level={level + 1}
              expanded={expanded}
              onToggle={onToggle}
            />
          ) : (
            <div className="relative flex items-start">
              <div
                className="absolute top-0 left-0 right-0 h-px bg-muted-foreground/30"
                style={{
                  left: `${100 / (node.children.length * 2)}%`,
                  right: `${100 / (node.children.length * 2)}%`,
                }}
              />
              <div className="flex gap-4">
                {node.children.map((child) => (
                  <div key={child.id} className="flex flex-col items-center">
                    <div className="w-px h-5 bg-muted-foreground/30" />
                    <OrgCard
                      node={child}
                      level={level + 1}
                      expanded={expanded}
                      onToggle={onToggle}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AreasPage() {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [tableName, setTableName] = useState<string>("");
  const [tree, setTree] = useState<TreeNode[]>([]);
  const [flatRows, setFlatRows] = useState<AreaRow[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const [fieldsRes, recordsRes] = await Promise.all([
        pipefyApi.listFields({ tableId: AREAS_DATABASE_ID }),
        pipefyApi.listTableRecords({ tableId: AREAS_DATABASE_ID }),
      ]);

      if (!recordsRes.success || !recordsRes.records) {
        toast.error(recordsRes.error || "Erro ao carregar registros");
        return;
      }
      if (recordsRes.table?.name) setTableName(recordsRes.table.name);
      const treeData = buildTree(recordsRes.records);
      setTree(treeData);
      setFlatRows(treeToFlatRows(treeData));
      if (treeData.length > 0 && expanded.size === 0) {
        const allIds: string[] = [];
        const collectIds = (nodes: TreeNode[]) => {
          nodes.forEach((n) => { allIds.push(n.id); collectIds(n.children); });
        };
        collectIds(treeData);
        setExpanded(new Set(allIds));
      }
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || "Erro ao carregar áreas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleToggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-4 p-6 w-full mx-auto overflow-x-hidden">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Áreas de Negócio</h2>
          <p className="text-muted-foreground text-sm">
            Hierarquia de áreas e subáreas sincronizada com o Pipefy (database 0. Áreas de Negócio).
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            setSyncing(true);
            load().finally(() => setSyncing(false));
          }}
          disabled={loading || syncing}
        >
          {(syncing || loading) ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Atualizar
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{tableName || "0. Áreas de Negócio"}</CardTitle>
          <CardDescription>
            Nível 1 = área raiz · Nível 2 = subárea (Diretoria) · Nível 3 = subnível (Área de
            Negócio vinculada)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : tree.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              Nenhum registro encontrado. Verifique o ID do database e o token do Pipefy.
            </p>
          ) : (
            <Tabs defaultValue="tabela" className="w-full">
              <TabsList className="grid w-full max-w-xs grid-cols-2">
                <TabsTrigger value="tabela" className="flex items-center gap-2">
                  <Table2 className="h-4 w-4" />
                  Tabela
                </TabsTrigger>
                <TabsTrigger value="organograma" className="flex items-center gap-2">
                  <GitBranch className="h-4 w-4" />
                  Organograma
                </TabsTrigger>
              </TabsList>

              <TabsContent value="tabela" className="mt-4">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Área de Negócio</TableHead>
                        <TableHead>Nível</TableHead>
                        <TableHead>Área pai</TableHead>
                        <TableHead>Diretor responsável</TableHead>
                        <TableHead>Gerente responsável</TableHead>
                        <TableHead>Usuário responsável</TableHead>
                        <TableHead>Observações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {flatRows.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell className="font-medium">{row.nome}</TableCell>
                          <TableCell>
                            <Badge
                              variant={row.nivel === "Nível 1" ? "default" : "secondary"}
                              className="text-xs"
                            >
                              {row.nivel}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {row.areaPai || "—"}
                          </TableCell>
                          <TableCell className="text-muted-foreground max-w-[180px] truncate" title={row.diretor}>
                            {row.diretor}
                          </TableCell>
                          <TableCell className="text-muted-foreground max-w-[180px] truncate" title={row.gerente}>
                            {row.gerente}
                          </TableCell>
                          <TableCell className="text-muted-foreground max-w-[180px] truncate" title={row.usuario}>
                            {row.usuario}
                          </TableCell>
                          <TableCell className="text-muted-foreground max-w-[200px] truncate" title={row.observacoes}>
                            {row.observacoes}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              <TabsContent value="organograma" className="mt-4">
                <p className="text-sm text-muted-foreground mb-3">
                  Clique em uma área para expandir ou recolher seus filhos.
                </p>
                <div className="rounded-xl border bg-muted/20 p-6 min-h-[400px] overflow-x-auto w-full max-w-[calc(100vw-4rem)] md:max-w-[calc(100vw-20rem)]">
                  <div className="inline-flex gap-8 pb-4 min-w-max">
                    {tree.map((node) => (
                      <OrgCard
                        key={node.id}
                        node={node}
                        level={0}
                        expanded={expanded}
                        onToggle={handleToggle}
                      />
                    ))}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
